import React from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { FN } from "./facilityNetworkTheme";

export type FacilityNetworkTabId =
  | "overview"
  | "hierarchy"
  | "children"
  | "link-request"
  | "unlink-request"
  | "requests";

export type FacilityNetworkTab = {
  id: FacilityNetworkTabId;
  label: string;
  count?: number;
};

type Props = {
  tabs: FacilityNetworkTab[];
  value: FacilityNetworkTabId;
  onChange: (id: FacilityNetworkTabId) => void;
  density?: "default" | "figma";
};

export default function FacilityNetworkTabBar({
  tabs,
  value,
  onChange,
  density = "default",
}: Props) {
  const index = Math.max(
    0,
    tabs.findIndex((t) => t.id === value),
  );

  return (
    <Box sx={{ py: FN.tabsVerticalPad }}>
      <Tabs
        value={index}
        onChange={(_, idx) => {
          const tab = tabs[idx as number];
          if (tab) onChange(tab.id);
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: FN.tabMinHeight,
          borderBottom: density === "figma" ? `1px solid ${FN.tabBarBorder}` : "none",
          "& .MuiTabs-indicator": { display: "none" },
          "& .MuiTab-root": {
            fontFamily: FN.fontFamily,
            textTransform: "none",
            fontSize: density === "figma" ? 15 : FN.tabFontSize,
            fontWeight: 400,
            minHeight: FN.tabMinHeight,
            px: 1.25,
            py: 0.5,
            color: FN.muted,
            borderRadius: 0,
            borderBottom: "2px solid transparent",
            "&.Mui-selected": {
              color: FN.activeTabBorder,
              fontWeight: 500,
              borderBottom: `2px solid ${FN.activeTabBorder}`,
            },
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <span>{tab.label}</span>
                {tab.count != null && (density === "figma" || tab.count > 0) ? (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 11,
                      color: FN.muted,
                      bgcolor: FN.countBadgeBg,
                      borderRadius: "4px",
                      px: 0.5,
                      minWidth: 20,
                      textAlign: "center",
                      lineHeight: 1.4,
                    }}
                  >
                    {tab.count}
                  </Typography>
                ) : null}
              </Box>
            }
          />
        ))}
      </Tabs>
    </Box>
  );
}
